<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        class="sl-radio"
        :tabindex="tabIndex"
        @keydown.up.prevent="selectPrevious()"
        @keydown.left.prevent="selectPrevious()"
        @keydown.right.prevent="selectNext()"
        @keydown.down.prevent="selectNext()"
        @keydown.space.prevent="toggle()"
    >
        <div class="sl-radio__items">
            <div
                v-for="item in items"
                :key="item.ovalue"
                class="sl-radio__item"
                :class="{
                    'sl-radio__item--selected': item.ovalue == modelValue,
                    'sl-radio__item--disabled': !item.is_enabled,
                }"
                @click="setValue(item)"
            >
                <!-- Picture -->
                <div v-if="item.picture" class="sl-radio__item-picture">
                    <img :src="getPictureSrc(item.picture)" alt="">
                </div>

                <div class="sl-radio__item-body">
                    <!-- Label -->
                    <div class="sl-radio__item-label" v-html="item.label"></div>

                    <!-- Help -->
                    <div
                        v-if="item.help"
                        v-tippy="{ animation : 'fade' }"
                        class="sl-radio__item-help"
                        :content="item.help"
                    >
                        <mdi-icon icon="mdiInformationOutline" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import useRadioButtonComposable from '@/composables/RadioButtonComposable';
import { useStore } from 'vuex';

// ------------------------------------------------------------ PROPS / EMITS

const props = defineProps({
    modelValue:
    {
        type:    [String, Number, Boolean, Object, null],
        default: null,
    },

    items:
    {
        type:     Array,
        required: true,
    },

    nullable:
    {
        type:    Boolean,
        default: false,
    },

    tabIndex:
    {
        type:    [Number, String, null],
        default: 0,
    },
});

const emit = defineEmits(['update:modelValue']);

// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const { getPictureSrc } = useRadioButtonComposable();

// ------------------------------------------------------------ COMPUTED

const cIndex = computed(() =>
    props.items.findIndex(i => i.ovalue == props.modelValue)
);

// ------------------------------------------------------------ METHODS

function setValue(item)
{
    if(!item.is_enabled)
    {
        return;
    }

    let val = item.ovalue;
    if(item.ovalue == props.modelValue && props.nullable)
    {
        // Allow de-selecting current value if nullable
        val = null;
    }

    emit('update:modelValue', val);
}

function selectPrevious()
{
    let prevIndex = cIndex.value;
    for(let i = (cIndex.value === -1 ? props.items.length : cIndex.value) - 1; i >= 0; i--)
    {
        if(props.items[i].is_enabled)
        {
            prevIndex = i;
            break;
        }
    }

    if(prevIndex !== cIndex.value)
    {
        setValue(props.items[prevIndex]);
    }
}

function selectNext()
{
    let nextIndex = cIndex.value;
    for(let i = cIndex.value + 1; i < props.items.length; i++)
    {
        if(props.items[i].is_enabled)
        {
            nextIndex = i;
            break;
        }
    }

    if(nextIndex !== cIndex.value)
    {
        setValue(props.items[nextIndex]);
    }
}

function toggle()
{
    if(props.modelValue !== null)
    {
        if(props.nullable)
        {
            emit('update:modelValue', null);
        }

        return;
    }

    selectNext();
}
</script>

<style scoped>
@reference "#main.css";

.sl-radio {
    &:focus {
        @apply outline-none ring-1 ring-primary-400;
    }
}

.sl-radio__items {
    @apply flex flex-row flex-wrap;
}

.sl-radio__item {
    @apply cursor-pointer flex flex-col justify-center items-center gap-0 grow
        m-px px-2 min-h-[30px] min-w-[30px]
        bg-white border border-gray-300 text-gray-600 text-sm text-center
        whitespace-nowrap select-none;

    z-index: unset;

    & img {
        @apply w-[50px] h-[25px];
    }

    &:not(.sl-radio__item--selected) {
        @apply text-gray-600 transition-colors;

        &:hover {
            @apply border-primary-300 bg-primary-50;
        }
    }

    &.sl-radio__item--selected {
        @apply border-primary-500 bg-primary-100 text-primary-600 z-10;
    }

    &.sl-radio__item--disabled {
        @apply bg-gray-200 text-gray-400;

        &:hover {
            @apply cursor-not-allowed border-gray-300 bg-gray-200 select-none;
        }
    }
}

.sl-radio__item-body {
    @apply flex flex-row justify-between items-center;
}

.sl-radio__item-help {
    @apply cursor-help flex justify-center items-center ml-1;

    &:focus {
        @apply outline-none;
    }

    & .mdi-icon {
        @apply w-4 h-4 text-gray-400;
    }
}
</style>
